import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { toBanglaNumber, formatCurrency } from '../../lib/utils';
import { CheckCircle2, Home, Printer } from 'lucide-react';
import { KioskScreen } from '../../components/kiosk/KioskScreen';
export function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    quantity = 1,
    total = 20,
    gateway = 'bkash'
  } = location.state || {};
  const [timeLeft, setTimeLeft] = useState(60);
  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const randomId = useMemo(() => Math.floor(Math.random() * 10000).toString().padStart(4, '0'), []);
  const ticketId = `CHMB-${dateStr}-${randomId}`;
  useEffect(() => {
    if (timeLeft > 0) {
      const t = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(t);
    } else {
      navigate('/');
    }
  }, [timeLeft, navigate]);
  const gName = gateway === 'bkash' ? 'বিকাশ' : 'নগদ';
  return <KioskScreen showFooter={false} data-id="element-592">
      <main className="flex-1 min-h-0 flex items-center justify-center px-6 py-3" data-id="element-593">
        <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-594">
          {/* Left — success message */}
          <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-xl border border-white/80 p-6 flex flex-col justify-center" data-id="element-595">
            <CheckCircle2 className="w-16 h-16 text-emerald-600 mb-3" data-id="element-596" />
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1" data-id="element-597">
              পেমেন্ট সফল হয়েছে
            </h2>
            <div className="flex items-center gap-2 text-brand-coral font-medium mb-4" data-id="element-598">
              <Printer className="w-4 h-4 animate-pulse" data-id="element-599" />
              <span className="text-sm" data-id="element-600">টিকিট প্রিন্ট হচ্ছে...</span>
            </div>

            <div className="space-y-2 text-sm border-t border-slate-100 pt-3" data-id="element-601">
              <div className="flex justify-between" data-id="element-602">
                <span className="text-slate-500" data-id="element-603">টিকিট আইডি</span>
                <span className="font-semibold text-slate-900" data-id="element-604">{ticketId}</span>
              </div>
              <div className="flex justify-between" data-id="element-605">
                <span className="text-slate-500" data-id="element-606">টিকিট সংখ্যা</span>
                <span className="font-semibold text-slate-900" data-id="element-607">
                  {toBanglaNumber(quantity)} জন
                </span>
              </div>
              <div className="flex justify-between" data-id="element-608">
                <span className="text-slate-500" data-id="element-609">পেমেন্ট মাধ্যম</span>
                <span className="font-semibold text-slate-900" data-id="element-610">{gName}</span>
              </div>
              <div className="flex justify-between border-t border-slate-100 pt-2" data-id="element-611">
                <span className="text-slate-500" data-id="element-612">মোট প্রদান</span>
                <span className="font-bold text-brand-coral text-base" data-id="element-613">
                  {formatCurrency(total)}
                </span>
              </div>
            </div>

            <p className="text-center text-xl font-bold text-slate-900 mt-4" data-id="element-614">
              ধন্যবাদ
            </p>
            <p className="text-center text-xs text-slate-500 mt-0.5" data-id="element-615">
              মূল পাতায় ফিরে যাবে {toBanglaNumber(timeLeft)} সেকেন্ড পর
            </p>

            <Button size="lg" className="w-full mt-3 bg-slate-900 hover:bg-slate-800" onClick={() => navigate('/')} data-id="element-616">
              <Home className="w-5 h-5 mr-2" data-id="element-617" />
              প্রথম পাতা
            </Button>
          </div>

          {/* Right — ticket preview */}
          <div className="bg-white rounded-3xl shadow-xl border-2 border-dashed border-slate-300 p-5 relative" data-id="element-618">
            <div className="text-center pb-3 border-b-2 border-dashed border-slate-200 mb-3" data-id="element-619">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-620">
                চিম্বুক পাহাড়
              </h3>
              <p className="text-xs text-slate-500" data-id="element-621">
                জেলা প্রশাসন, বান্দরবন পার্বত্য জেলা
              </p>
            </div>

            <div className="space-y-1.5 text-sm mb-3" data-id="element-622">
              <div className="flex justify-between" data-id="element-623">
                <span className="text-slate-500" data-id="element-624">Ticket ID</span>
                <span className="font-mono font-semibold text-slate-900 text-xs" data-id="element-625">
                  {ticketId}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-626">
                <span className="text-slate-500" data-id="element-627">তারিখ</span>
                <span className="font-medium text-slate-900 text-xs" data-id="element-628">
                  {new Date().toLocaleDateString('bn-BD')}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-629">
                <span className="text-slate-500" data-id="element-630">সময়</span>
                <span className="font-medium text-slate-900 text-xs" data-id="element-631">
                  {new Date().toLocaleTimeString('bn-BD')}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-632">
                <span className="text-slate-500" data-id="element-633">দর্শনার্থী</span>
                <span className="font-semibold text-slate-900" data-id="element-634">
                  {toBanglaNumber(quantity)} জন
                </span>
              </div>
              <div className="flex justify-between" data-id="element-635">
                <span className="text-slate-500" data-id="element-636">পেমেন্ট</span>
                <span className="font-medium text-slate-900" data-id="element-637">{gName}</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t-2 border-dashed border-slate-200" data-id="element-638">
              <div data-id="element-639">
                <p className="text-xs text-slate-500" data-id="element-640">মোট</p>
                <p className="text-2xl font-bold text-slate-900" data-id="element-641">
                  {formatCurrency(total)}
                </p>
              </div>
              {/* QR placeholder */}
              <div className="w-20 h-20 bg-white border-2 border-slate-900 rounded p-1 grid grid-cols-8 grid-rows-8 gap-px" data-id="element-642">
                {Array.from({
                length: 64
              }).map((_, i) => <div key={i} className={Math.random() > 0.5 ? 'bg-slate-900' : 'bg-white'} data-id="element-643" />)}
              </div>
            </div>
          </div>
        </div>
      </main>
    </KioskScreen>;
}